/**
 * Inline formatting inside a plain text field.
 *
 * The Rich text block is gone, and the whole-block controls that replaced it (size, weight,
 * italic, underline) cannot do the single most common thing in body copy: emphasise *part* of
 * a sentence. Rather than bring back a second editor with its own value format — the one that
 * produced the `[object Object]` bug — the Text block keeps a plain string and this parser
 * turns a tiny, familiar markup into real IR children.
 *
 * Deliberately four rules and no more. `**bold**`, `*italic*`, `[text](url)` and a literal
 * backslash escape. No headings, no lists, no tables: the block library already has proper
 * blocks for those, and each extra rule is another thing that can surprise someone who typed
 * an asterisk by accident.
 *
 * Because the output is IR, the emphasis survives into Webflow as native `Strong`, `Emphasized`
 * and `Link` nodes — editable in the Designer, not frozen inside an embed.
 */

import { el, text as textNode, type IRNode } from '@lp/shared';

/** `**bold**` · `*italic*` · `[label](href)` · `\*` for a literal asterisk. */
const PATTERN = /\\([\\*[\]])|\*\*([\s\S]+?)\*\*|\*([^*\n]+?)\*|\[([^\]\n]+?)\]\(([^)\s]+)\)/g;

const strong = (children: IRNode[]): IRNode =>
  el('Strong', 'strong', [{ name: 'lp-strong' }], children);

const emphasis = (children: IRNode[]): IRNode =>
  el('Emphasized', 'em', [{ name: 'lp-em' }], children);

const link = (label: string, href: string): IRNode =>
  el('Link', 'a', [{ name: 'lp-link' }], [textNode(label)], { attr: { href } });

/**
 * Split a string into IR children.
 *
 * Returns a single text node when there is no markup, which keeps the common case producing
 * exactly the tree it produced before this existed — no extra wrapper, no golden-file churn.
 */
export function parseInline(value: string): IRNode[] {
  if (!value) return [];
  // Cheap bail-out: most copy contains none of these characters at all.
  if (!/[*[\\]/.test(value)) return [textNode(value)];

  const out: IRNode[] = [];
  let last = 0;

  PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = PATTERN.exec(value)) !== null) {
    if (match.index > last) out.push(textNode(value.slice(last, match.index)));

    const [, escaped, bold, italic, label, href] = match;
    if (escaped !== undefined) {
      out.push(textNode(escaped));
    } else if (bold !== undefined) {
      // Nested, so `**bold with *emphasis* inside**` works.
      out.push(strong(parseInline(bold)));
    } else if (italic !== undefined) {
      out.push(emphasis(parseInline(italic)));
    } else if (label !== undefined && href !== undefined) {
      out.push(link(label, href));
    }

    last = match.index + match[0].length;
  }

  if (last < value.length) out.push(textNode(value.slice(last)));
  return out.length ? out : [textNode(value)];
}

/** The markup stripped out, for the SEO and voice linters — they want words, not syntax. */
export function plainInline(value: string): string {
  return value
    .replace(/\\([\\*[\]])/g, '$1')
    .replace(/\*\*([\s\S]+?)\*\*/g, '$1')
    .replace(/\*([^*\n]+?)\*/g, '$1')
    .replace(/\[([^\]\n]+?)\]\(([^)\s]+)\)/g, '$1');
}

/** Any links the copy contains, so the internal-link audit can see them. */
export function inlineLinks(value: string): Array<{ label: string; href: string }> {
  const out: Array<{ label: string; href: string }> = [];
  const re = /\[([^\]\n]+?)\]\(([^)\s]+)\)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(value)) !== null) out.push({ label: match[1]!, href: match[2]! });
  return out;
}

/** Shown under the field, so the syntax is discoverable rather than folklore. */
export const INLINE_HELP = '**bold**, *italic*, [link](https://…)';
